import { usePlayerStore } from '@/store/playerStore';
import { Play, Pause, SkipBack, SkipForward, Volume2, VolumeX, Music, X } from 'lucide-react';
import { Slider } from '@/components/ui/slider';

export function GlobalFooterPlayer() {
  const { 
    currentSong, 
    isPlaying, 
    progress, 
    duration,
    volume,
    pauseSong, 
    resumeSong,
    seekToPosition,
    setVolume,
    isPlayerVisible,
    hidePlayer
  } = usePlayerStore();
  
  // İlerleme çubuğundaki değeri % olarak hesapla
  const progressPercentage = duration > 0 ? (progress / duration) * 100 : 0;
  
  // Zaman formatı
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };
  
  // Önceki veya sonraki şarkıya geçme fonksiyonu
  const handlePreviousTrack = () => {
    // Başa sar
    seekToPosition(0);
  };
  
  const handleNextTrack = () => {
    // Sona git
    if (duration > 0) {
      seekToPosition(duration);
    }
  };
  
  // İlerleme çubuğunu sürükleme olayları
  const handleProgressChange = (value: number[]) => {
    if (duration > 0) {
      const newPosition = (value[0] / 100) * duration;
      seekToPosition(newPosition);
    }
  };
  
  // Ses seviyesi değişimi
  const handleVolumeChange = (value: number) => {
    setVolume(value / 100);
  };
  
  if (!currentSong || !isPlayerVisible) return null;
  
  return (
    <footer className="fixed bottom-0 left-0 right-0 bg-background border-t z-40 py-2 px-4">
      <div className="max-w-full mx-auto flex items-center">
        {/* Şarkı bilgileri */}
        <div className="w-1/4 flex items-center gap-3 min-w-[180px]">
          {currentSong.artworkUrl ? (
            <img 
              src={currentSong.artworkUrl} 
              alt={currentSong.name}
              className="h-10 w-10 rounded-sm object-cover"
            />
          ) : (
            <div className="h-10 w-10 rounded-sm bg-primary/10 flex items-center justify-center">
              <Music className="h-5 w-5 text-primary" />
            </div>
          )}
          <div className="truncate">
            <p className="text-sm font-medium truncate">{currentSong.name}</p>
            <p className="text-xs text-muted-foreground truncate">{currentSong.artist || 'Sanatçı yok'}</p>
          </div>
        </div>
        
        {/* Çalma kontrolleri */}
        <div className="flex-1 flex flex-col items-center justify-center">
          <div className="flex items-center gap-4 mb-1">
            <button 
              onClick={handlePreviousTrack}
              className="p-1 hover:text-primary"
              title="Başa sar"
            >
              <SkipBack size={18} />
            </button>
            <button
              onClick={() => isPlaying ? pauseSong() : resumeSong()}
              className="p-1 hover:text-primary"
            >
              {isPlaying ? <Pause size={18} /> : <Play size={18} />}
            </button>
            <button 
              onClick={handleNextTrack}
              className="p-1 hover:text-primary"
              title="Sona git"
            >
              <SkipForward size={18} />
            </button>
          </div>
          
          {/* İlerleme çubuğu */}
          <div className="w-full max-w-md flex items-center gap-2">
            <span className="text-xs text-muted-foreground min-w-[40px] text-right">
              {formatTime(progress)}
            </span>
            <Slider
              value={[progressPercentage]}
              min={0}
              max={100}
              step={0.1}
              onValueChange={handleProgressChange}
              className="cursor-pointer"
            />
            <span className="text-xs text-muted-foreground min-w-[40px]">
              {formatTime(duration)}
            </span>
          </div>
        </div>
        
        {/* Ses kontrolü ve kapatma butonu */}
        <div className="w-1/4 flex justify-end items-center gap-3">
          {/* Ses kontrolü */}
          <div className="flex items-center gap-2">
            <button 
              onClick={() => setVolume(volume === 0 ? 0.5 : 0)}
              className="p-1 hover:text-primary"
              title={volume === 0 ? "Sesi Aç" : "Sesi Kapat"}
            >
              {volume === 0 ? <VolumeX size={18} /> : <Volume2 size={18} />}
            </button>
            
            <div className="w-20 hidden md:block">
              <Slider
                value={[volume * 100]}
                min={0}
                max={100}
                step={1}
                onValueChange={values => handleVolumeChange(values[0])}
                orientation="horizontal"
              />
            </div>
          </div>
          
          {/* Kapatma butonu */}
          <button
            onClick={() => {
              hidePlayer();
              // Ayrıca audio elementi de durduralım
              const audioElement = document.querySelector('audio');
              if (audioElement) {
                audioElement.pause();
                audioElement.currentTime = 0;
              }
            }}
            className="p-1 hover:text-red-500 ml-2"
            title="Oynatıcıyı Kapat"
          >
            <X size={18} />
          </button>
        </div>
      </div>
    </footer>
  );
}
